import React, { useState, useMemo, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Custom hooks
import useMessageData from "layouts/tables/data/smsTableData";

function Message() {
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .swal2-container {
        z-index: 9999 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const {
    columns,
    rows,
    isLoading,
    error,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedMessage,
    setSelectedMessage,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddMessage,
    members,
    sendBulkMessages,
  } = useMessageData();

  const [messageType, setMessageType] = useState("single");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [isSending, setIsSending] = useState(false); // New loading state

  const allSelected = useMemo(() => {
    return members.length > 0 && selectedMembers.length === members.length;
  }, [members, selectedMembers]);

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(members.map((member) => member.id));
    }
  };

  const handleMemberSelection = (event) => {
    const value = event.target.value;
    if (value[value.length - 1] === "select-all") {
      setSelectedMembers(allSelected ? [] : members.map((member) => member.id));
      return;
    }
    setSelectedMembers(value);
  };

  const handleMessageTypeChange = (event) => {
    setMessageType(event.target.value);
    if (event.target.value === "single") {
      setSelectedMembers([]);
    }
  };

  const handleSubmit = async () => {
    setIsSending(true); // Start loading

    try {
      if (messageType === "bulk") {
        if (selectedMembers.length === 0) {
          Swal.fire({
            title: "No Recipients Selected",
            text: "Please select at least one recipient",
            icon: "warning",
            confirmButtonText: "OK",
          });
          return;
        }
        if (!selectedMessage?.message) {
          Swal.fire({
            title: "Message Required",
            text: "Please enter a message to send",
            icon: "warning",
            confirmButtonText: "OK",
          });
          return;
        }
        await sendBulkMessages(
          selectedMembers,
          selectedMessage.message,
          selectedMessage.type || "bulk"
        );
      } else {
        if (!selectedMessage?.recipient || !selectedMessage?.message) {
          Swal.fire({
            title: "Incomplete Information",
            text: !selectedMessage?.recipient
              ? "Please select a recipient"
              : "Please enter a message",
            icon: "warning",
            confirmButtonText: "OK",
          });
          return;
        }

        const messageToSend = {
          ...selectedMessage,
          recipient: selectedMessage.recipient,
          type: selectedMessage.type || "single",
        };

        await handleEditSubmit(messageToSend);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to send message. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSending(false); // Stop loading regardless of success/failure
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDTypography>Loading Messages...</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDTypography color="error">Error: {error}</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <Grid container alignItems="center" justifyContent="space-between">
                  <Grid item>
                    <MDTypography variant="h6" color="white">
                      SMS Messages
                    </MDTypography>
                  </Grid>
                  <Grid item>
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <MDButton variant="gradient" color="dark" onClick={handleAddMessage}>
                        <Icon>add</Icon>&nbsp;Send Message
                      </MDButton>
                      <MDButton variant="gradient" color="dark" onClick={handlePrint}>
                        <Icon>print</Icon>
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={true}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={true}
                  searchLabel="Search messages..."
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Message Modal */}
      <Dialog
        open={editModalOpen}
        onClose={() => !isSending && setEditModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDTypography variant="h6" gutterBottom>
            {selectedMessage?.id ? "Edit Message" : "Send New Message"}
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDBox p={3}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="message-type-label">Message Type</InputLabel>
                  <Select
                    labelId="message-type-label"
                    id="message-type"
                    value={messageType}
                    label="Message Type"
                    onChange={handleMessageTypeChange}
                    sx={{ height: 40 }}
                    disabled={isSending}
                  >
                    <MenuItem value="single">Single Message</MenuItem>
                    <MenuItem value="bulk">Bulk Message</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {messageType === "single" && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="recipient-label">Recipient</InputLabel>
                    <Select
                      labelId="recipient-label"
                      id="recipient"
                      value={selectedMessage?.member_id || ""}
                      label="Recipient"
                      onChange={(e) => {
                        const selectedMember = members.find((m) => m.id === e.target.value);
                        setSelectedMessage({
                          ...selectedMessage,
                          member_id: e.target.value,
                          recipient: selectedMember?.phone || "",
                        });
                      }}
                      sx={{ height: 40 }}
                      disabled={isSending}
                    >
                      {members.map((member) => (
                        <MenuItem key={member.id} value={member.id}>
                          {member.first_name} {member.surname} ({member.phone})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {messageType === "bulk" && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="recipients-label">Recipients</InputLabel>
                    <Select
                      labelId="recipients-label"
                      id="recipients"
                      multiple
                      value={selectedMembers}
                      onChange={handleMemberSelection}
                      renderValue={(selected) => `${selected.length} members selected`}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                          },
                        },
                      }}
                      sx={{ height: 40 }}
                      disabled={isSending}
                    >
                      <MenuItem value="select-all">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={selectedMembers.length > 0 && !allSelected}
                          disabled={isSending}
                        />
                        <ListItemText primary="Select All" />
                      </MenuItem>
                      {members.map((member) => (
                        <MenuItem key={member.id} value={member.id}>
                          <Checkbox
                            checked={selectedMembers.indexOf(member.id) > -1}
                            disabled={isSending}
                          />
                          <ListItemText
                            primary={`${member.first_name} ${member.surname} (${member.phone})`}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  label="Message"
                  multiline
                  rows={6}
                  value={selectedMessage?.message || ""}
                  onChange={(e) =>
                    setSelectedMessage({
                      ...selectedMessage,
                      message: e.target.value,
                    })
                  }
                  fullWidth
                  variant="outlined"
                  required
                  disabled={isSending}
                />
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="gradient"
            color="secondary"
            onClick={() => {
              if (!isSending) {
                setEditModalOpen(false);
                setSelectedMembers([]);
              }
            }}
            mr={2}
            disabled={isSending}
          >
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={handleSubmit} disabled={isSending}>
            {isSending ? (
              <CircularProgress size={24} color="inherit" />
            ) : selectedMessage?.id ? (
              "Update Message"
            ) : (
              "Send Message"
            )}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Delete Message Modal */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <MDBox p={3}>
          <MDTypography variant="h6" gutterBottom>
            Confirm Delete
          </MDTypography>
          <MDTypography>Are you sure you want to delete this message?</MDTypography>
          <MDBox display="flex" justifyContent="flex-end" mt={3}>
            <MDButton
              variant="gradient"
              color="secondary"
              onClick={() => setDeleteModalOpen(false)}
              mr={2}
            >
              Cancel
            </MDButton>
            <MDButton
              style={{ marginLeft: 10 }}
              variant="gradient"
              color="error"
              onClick={handleDeleteConfirm}
            >
              Confirm Delete
            </MDButton>
          </MDBox>
        </MDBox>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

Message.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  handlePrint: PropTypes.func,
  editModalOpen: PropTypes.bool,
  setEditModalOpen: PropTypes.func,
  deleteModalOpen: PropTypes.bool,
  setDeleteModalOpen: PropTypes.func,
  selectedMessage: PropTypes.object,
  setSelectedMessage: PropTypes.func,
  handleEditSubmit: PropTypes.func,
  handleDeleteConfirm: PropTypes.func,
  handleAddMessage: PropTypes.func,
  members: PropTypes.arrayOf(PropTypes.object),
};

export default Message;
